import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useApp } from '@/contexts/AppContext';
import { supabase } from '@/integrations/supabase/client';
import { invokeAuthedFunction } from '@/lib/invokeAuthedFunction';
import { getFunctionsErrorBody, formatFunctionsError } from '@/lib/functions-error';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Zap,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  Loader2,
  MapPin,
  Battery,
  Cpu,
  Building,
  Eye,
  EyeOff,
  RefreshCw,
  Download,
  BarChart3,
} from 'lucide-react';
import { BranchSelector, type OnboardingBranch } from '@/components/onboarding/BranchSelector';
import { ColdStartFlow } from '@/components/onboarding/ColdStartFlow';

// Canonical fleet onboarding: Branch Select → Login → Discover → Preview & Select → Import → Activation → Add Another Login → Notification Defaults → Complete
type WizardStep = 'branch_select' | 'cold_start' | 'credentials' | 'discover' | 'confirm' | 'import' | 'activation' | 'add_another_login' | 'notification_preferences' | 'complete';

interface DiscoveredPlant {
  id: string;
  name: string;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  timezone: string | null;
  timezoneOffset?: number;
  batteryCapacityKwh: number | null;
  installedCapacityKw: number;
  createdAt: string | null;
  selected: boolean;
  /** True when this plant is already in the platform (same org, same provider_plant_id). Shown greyed out and pre-deselected. */
  existingInPlatform?: boolean;
  inverters: DiscoveredInverter[];
}

interface DiscoveredInverter {
  sn: string;
  alias: string;
  model: string;
  ratePower: number;
  role: 'primary' | 'secondary' | 'standalone';
  isOnline: boolean;
}

interface ImportedProperty {
  id: string;
  name: string;
  plantId: string;
  systemIds: string[];
}

/** From get_import_job_status RPC */
interface ImportJobStatus {
  job_id: string;
  status: string;
  total_plants: number;
  processed_success: number;
  failed_count: number;
  pending_count: number;
  processing_count: number;
  complete_count: number;
  done_count: number;
  remaining_count: number;
  updated_at: string;
  org_id: string;
}

export default function OnboardingV2() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refreshData, organization } = useApp();
  
  const [step, setStep] = useState<WizardStep>('branch_select');
  const [selectedBranch, setSelectedBranch] = useState<OnboardingBranch | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Provider (Sol-Ark, EG4, FranklinWH, SolarEdge)
  const [provider, setProvider] = useState<'solark' | 'eg4' | 'franklinwh' | 'solaredge' | 'solis'>('solark');

  // Credentials (username/password for most; API key + account label for SolarEdge)
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [solaredgeAccount, setSolaredgeAccount] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // Organization (only if new user)
  const [orgName, setOrgName] = useState('');
  const [orgType, setOrgType] = useState<'homeowner' | 'installer' | 'property_manager'>('homeowner');

  // Discovered data
  const [plants, setPlants] = useState<DiscoveredPlant[]>([]);
  
  // Import results
  const [importedProperties, setImportedProperties] = useState<ImportedProperty[]>([]);
  const [importProgress, setImportProgress] = useState(0);
  const [importJobId, setImportJobId] = useState<string | null>(null);
  const [importStatus, setImportStatus] = useState<ImportJobStatus | null>(null);
  const [pollTrigger, setPollTrigger] = useState(0);
  const [importError, setImportError] = useState<string | null>(null);
  const importPollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [activationBackfillStarted, setActivationBackfillStarted] = useState(false);
  const [backfillStartedForPropertyIds, setBackfillStartedForPropertyIds] = useState<Set<string>>(new Set());

  // Notification defaults step (optional email/phone)
  const [notificationEmail, setNotificationEmail] = useState('');
  const [notificationPhone, setNotificationPhone] = useState('');

  // EG4: grid-tied flag from installer confirmation (EG4 Modbus registers don't expose grid-tied status — remains installer-provided)
  const [eg4GridTied, setEg4GridTied] = useState(false);

  const steps: { key: WizardStep; label: string }[] = [
    { key: 'credentials', label: 'Connect' },
    { key: 'discover', label: 'Discover' },
    { key: 'confirm', label: 'Confirm' },
    { key: 'import', label: 'Import' },
    { key: 'activation', label: 'Activation' },
    { key: 'add_another_login', label: 'Next' },
    { key: 'notification_preferences', label: 'Notifications' },
    { key: 'complete', label: 'Done' },
  ];

  const currentStepIndex = steps.findIndex(s => s.key === step);
  const progressPercent = ((currentStepIndex + 1) / steps.length) * 100;

  // Activation (Step 5): start 30-day backfill in background when entering step. No blocking; user can Continue immediately.
  useEffect(() => {
    const list = Array.isArray(importedProperties) ? importedProperties : [];
    if (step !== 'activation' || list.length === 0) return;
    const toStart = list.filter((p) => !backfillStartedForPropertyIds.has(p.id));
    if (toStart.length === 0) return;
    let cancelled = false;
    (async () => {
      for (const prop of toStart) {
        if (cancelled) return;
        try {
          const { data, error: createErr } = await supabase.functions.invoke<{ jobId?: string; existingJobId?: string }>('backfill-job', {
            body: { action: 'create', propertyId: prop.id, plantId: prop.plantId, rangePreset: '30days' },
          });
          const jobId = data?.jobId;
          const existingJobId = data?.existingJobId;
          const is409 = existingJobId != null || (createErr as { status?: number })?.status === 409;
          if (jobId) {
            await supabase.functions.invoke('backfill-job', { body: { action: 'start', jobId } });
          } else if (existingJobId) {
            await supabase.functions.invoke('backfill-job', { body: { action: 'start', jobId: existingJobId } });
          }
          if (jobId || is409) {
            if (!cancelled) setBackfillStartedForPropertyIds((prev) => new Set(prev).add(prop.id));
          }
        } catch {
          // Non-blocking; backfill can be retried from Settings
        }
      }
      if (!cancelled) setActivationBackfillStarted(true);
    })();
    return () => { cancelled = true; };
  }, [step, importedProperties, backfillStartedForPropertyIds]);

  // Import step: poll worker + get_import_job_status until status === 'complete'
  useEffect(() => {
    if (step !== 'import' || !importJobId) return;
    let cancelled = false;
    (async () => {
      try {
        setImportError(null);
        // Worker runs via cron (onboarding-import-worker); do not invoke from browser.
        const { data: statusData, error: statusErr } = await supabase.rpc('get_import_job_status', {
          p_job_id: importJobId,
        });
        if (cancelled) return;
        if (statusErr || statusData == null) {
          setImportError('We couldn\'t finish importing yet. Try again.');
          return;
        }
        const status = statusData as ImportJobStatus;
        setImportStatus(status);
        setImportProgress(status.total_plants > 0 ? Math.round((status.done_count / status.total_plants) * 100) : 0);
        if (status.status === 'complete') {
          const orgId = status.org_id;
          const { data: propsWithRelations } = await supabase
            .from('properties')
            .select(`
              id,
              name,
              plants (
                id,
                energy_systems ( id )
              )
            `)
            .eq('org_id', orgId);
          const props = Array.isArray(propsWithRelations) ? propsWithRelations : [];
          const built: ImportedProperty[] = props.map((p: { id: string; name: string; plants?: { id: string; energy_systems?: { id: string }[] }[] }) => {
            const plantsList = Array.isArray(p.plants) ? p.plants : [];
            const plantId = plantsList[0]?.id ?? '';
            const systemIds = plantsList.flatMap((pl) => Array.isArray(pl.energy_systems) ? pl.energy_systems.map((es) => es.id) : []);
            return { id: p.id, name: p.name, plantId, systemIds };
          });
          setImportedProperties(built);
          for (const prop of built) {
            if (prop.plantId) {
              try {
                await invokeAuthedFunction(supabase, 'property-auto-configure', {
                  propertyId: prop.id,
                  plantId: prop.plantId,
                });
              } catch {
                // Non-blocking
              }
            }
          }
          await refreshData();
          setImportProgress(100);
          setStep('activation');
          setLoading(false);
          setImportJobId(null);
          setImportStatus(null);
          return;
        }
        const delay = 750 + Math.round(Math.random() * 750);
        importPollTimeoutRef.current = setTimeout(() => {
          if (!cancelled) setPollTrigger((n) => n + 1);
        }, delay);
      } catch (err) {
        if (!cancelled) setImportError('We couldn\'t finish importing yet. Try again.');
      }
    })();
    return () => {
      cancelled = true;
      if (importPollTimeoutRef.current) {
        clearTimeout(importPollTimeoutRef.current);
        importPollTimeoutRef.current = null;
      }
    };
  }, [step, importJobId, pollTrigger, refreshData]);

  const handleDiscover = async () => {
    if (provider === 'solaredge') {
      if (!apiKey.trim()) {
        setError('Please enter your SolarEdge API key');
        return;
      }
    } else if (!username.trim() || !password.trim()) {
      setError(
        provider === 'eg4' ? 'Please enter your EG4 Monitor email and password'
        : provider === 'franklinwh' ? 'Please enter your FranklinWH email and password'
        : 'Please enter your Sol-Ark Cloud username and password'
      );
      return;
    }
    setLoading(true);
    setError(null);
    setStep('discover');

    try {
      const discoverFnMap: Record<string, string> = {
        eg4: 'eg4-discover',
        solark: 'solark-discover',
        franklinwh: 'franklinwh-discover',
        solaredge: 'solaredge-discover',
        solis: 'solis-discover',
      };
      const discoverFn = discoverFnMap[provider] ?? 'solark-discover';
      const body = provider === 'solaredge'
        ? { apiKey: apiKey.trim() }
        : { email: username.trim(), password: password.trim(), username: username.trim() };
      const { data, error: funcError } = await supabase.functions.invoke(discoverFn, { body });

      if (funcError) {
        const body = await getFunctionsErrorBody(funcError);
        const message = formatFunctionsError(body);
        throw new Error(message || funcError.message || 'Discovery failed');
      }

      if (!data?.success) {
        throw new Error((data as { error?: string })?.error || 'Discovery failed');
      }
      
      console.log('[OnboardingV2] Discovered:', data);
      
      // Group inverters by plant (same shape for Sol-Ark and EG4). Do not assume properties/plants/inverters exist.
      const plantMap = new Map<string, DiscoveredPlant>();
      const plantsList = Array.isArray(data?.plants) ? data.plants : [];
      const invertersList = Array.isArray(data?.inverters) ? data.inverters : [];

      for (const plant of plantsList) {
        plantMap.set(plant.id, {
          ...plant,
          selected: true,
          inverters: [],
        });
      }

      for (const inverter of invertersList) {
        const inv = { ...inverter, role: (inverter.role ?? 'standalone') as 'primary' | 'secondary' | 'standalone' };
        const plant = plantMap.get(inverter.plantId);
        if (plant) {
          plant.inverters.push(inv);
        }
      }

      let discoveredPlants = Array.from(plantMap.values());

      // If user has an org, mark plants already in the platform and sort: new first, existing below (pre-select new, deselect existing).
      const providerType = provider;
      if (organization?.id) {
        const { data: orgProperties } = await supabase
          .from('properties')
          .select('id')
          .eq('org_id', organization.id);
        const propertyIds = (orgProperties ?? []).map((p) => p.id);
        let existingProviderPlantIds = new Set<string>();
        if (propertyIds.length > 0) {
          const { data: orgPlants } = await supabase
            .from('plants')
            .select('provider_plant_id')
            .in('property_id', propertyIds)
            .eq('provider_type', providerType);
          existingProviderPlantIds = new Set(
            (orgPlants ?? []).map((p) => p.provider_plant_id).filter((id): id is string => id != null)
          );
        }
        for (const plant of discoveredPlants) {
          (plant as DiscoveredPlant).existingInPlatform = existingProviderPlantIds.has(plant.id);
          (plant as DiscoveredPlant).selected = !(plant as DiscoveredPlant).existingInPlatform;
        }
        discoveredPlants = [...discoveredPlants].sort((a, b) => {
          const aExisting = (a as DiscoveredPlant).existingInPlatform ?? false;
          const bExisting = (b as DiscoveredPlant).existingInPlatform ?? false;
          return (aExisting ? 1 : 0) - (bExisting ? 1 : 0);
        });
      } else {
        for (const plant of discoveredPlants) {
          (plant as DiscoveredPlant).existingInPlatform = false;
        }
      }

      if (discoveredPlants.length === 0) {
        const noSitesMsg: Record<string, string> = {
          eg4: 'No sites found in your EG4 account. Please ensure your inverters are registered.',
          franklinwh: 'No gateways found in your FranklinWH account. Please ensure your system is registered.',
          solark: 'No plants found in your Sol-Ark account. Please ensure your inverters are registered.',
          solaredge: 'No sites found for this API key. Check that the key has access to the monitoring portal.',
        };
        setError(noSitesMsg[provider] ?? noSitesMsg.solark);
        setPlants([]);
        setStep('confirm');
      } else {
        setError(null);
        setPlants(discoveredPlants);
        setStep('confirm');
        if (!orgName && discoveredPlants.length === 1) {
          setOrgName(discoveredPlants[0].name);
        }
      }

    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Discovery failed';
      // Never surface technical errors (HTTP codes, paths, JSON). Use generic message unless already human-safe (e.g. validation).
      const humanSafe =
        message === "We couldn't retrieve systems yet. Credentials are valid." ||
        message.includes('Missing username') ||
        message.includes('password') ||
        message.includes('Invalid email or password') ||
        message.includes('EG4 did not return') ||
        message.includes('Cannot reach EG4') ||
        message.includes('Account locked') ||
        message.includes('FranklinWH') ||
        message.includes('SolarEdge') ||
        message.includes('API key');
      setError(humanSafe ? message : "We couldn't retrieve systems yet. Credentials are valid.");
      setStep('credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePlant = (plantId: string) => {
    setPlants(plants.map(p =>
      p.id === plantId && !p.existingInPlatform ? { ...p, selected: !p.selected } : p
    ));
  };

  const handleImport = async () => {
    const selectedPlants = plants.filter(p => p.selected);

    if (selectedPlants.length === 0) {
      setError('Please select at least one plant to import');
      return;
    }

    if (!organization && !orgName.trim()) {
      setError('Please enter an organization name');
      return;
    }

    setLoading(true);
    setError(null);
    setImportError(null);
    setImportStatus(null);
    setImportProgress(0);
    setStep('import');

    try {
      const plantsToImport = selectedPlants.map(plant => ({
        providerPlantId: plant.id,
        name: plant.name,
        address: plant.address,
        latitude: plant.latitude,
        longitude: plant.longitude,
        timezone: plant.timezone,
        ...(plant.timezoneOffset !== undefined && { timezoneOffset: plant.timezoneOffset }),
        batteryCapacityKwh: plant.batteryCapacityKwh,
        ...(plant.createdAt && { createdAt: plant.createdAt }),
        ...(provider === 'eg4' && { gridTied: eg4GridTied }),
        inverters: plant.inverters.map(inv => ({
          sn: inv.sn,
          alias: inv.alias,
          role: inv.role,
          ratePower: inv.ratePower,
        })),
      }));

      const payload = {
        orgName: organization?.name || orgName.trim(),
        orgType,
        providerType: provider,
        plants: plantsToImport,
        credentials: provider === 'solaredge'
          ? { apiKey: apiKey.trim(), accountName: solaredgeAccount.trim() || undefined }
          : { username: username.trim(), password: password.trim() },
      };

      const data = await invokeAuthedFunction<{ success: boolean; jobId?: string; error?: string }>(
        supabase,
        'onboarding-import',
        payload
      );

      const result = data;
      if (!result.success || !result.jobId) {
        throw new Error(result.error || 'Import failed');
      }

      setImportJobId(result.jobId);
      setPollTrigger((n) => n + 1);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Import failed';
      setError(message);
      setStep('confirm');
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    const list = Array.isArray(importedProperties) ? importedProperties : [];
    const propertyIds = list.map((p) => p.id);
    if (propertyIds.length === 0) {
      navigate('/app', { replace: true });
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setError('Session expired. Please sign in again.');
        return;
      }
      await invokeAuthedFunction(supabase, 'complete-onboarding', { propertyIds });
      await refreshData();
      if (propertyIds.length === 1) {
        navigate(`/app/property/${propertyIds[0]}`, { replace: true });
      } else {
        navigate('/app', { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete onboarding');
    } finally {
      setLoading(false);
    }
  };

  const selectedCount = plants.filter(p => p.selected).length;
  const totalInverters = plants.filter(p => p.selected).reduce((sum, p) => sum + p.inverters.length, 0);

  const showCloudStepper = step !== 'branch_select' && step !== 'cold_start';

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      {/* Progress Header (cloud flow only — branch select and cold start have their own UI) */}
      {showCloudStepper && (
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold">Setup Wizard</h1>
          <span className="text-sm text-muted-foreground">
            Step {currentStepIndex + 1} of {steps.length}
          </span>
        </div>
        <Progress value={progressPercent} className="h-2" />
        <div className="flex justify-between mt-2">
          {steps.map((s, i) => (
            <span
              key={s.key}
              className={`text-xs ${i <= currentStepIndex ? 'text-primary' : 'text-muted-foreground'}`}
            >
              {s.label}
            </span>
          ))}
        </div>
      </div>
      )}

      {error && (
        <div className="mb-6 p-4 rounded-md bg-destructive/10 border border-destructive/20 text-destructive flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Step 0: Branch Select */}
      {step === 'branch_select' && (
        <BranchSelector
          onSelect={(branch: OnboardingBranch) => {
            if (branch === 'cloud') {
              setStep('credentials');
            } else {
              setSelectedBranch(branch);
              setStep('cold_start');
            }
          }}
        />
      )}

      {/* Step 0b: Cold Start Flow (Branch B: gateway_primary, Branch C: gateway_only) */}
      {step === 'cold_start' && (
        <ColdStartFlow
          onBack={() => setStep('branch_select')}
          dataSourceMode={selectedBranch === 'gateway' ? 'gateway_primary' : 'gateway_only'}
        />
      )}

      {/* Step 1: Credentials */}
      {step === 'credentials' && (
        <Card className="bg-wt-bg-secondary border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-primary" />
              {provider === 'eg4' ? 'Connect to EG4 Monitor'
                : provider === 'franklinwh' ? 'Connect to FranklinWH'
                : provider === 'solaredge' ? 'Connect to SolarEdge'
                : 'Connect to Sol-Ark Cloud'}
            </CardTitle>
            <CardDescription>
              {provider === 'eg4'
                ? 'Enter your EG4 Monitor credentials (monitor.eg4electronics.com) to discover your sites and inverters.'
                : provider === 'franklinwh'
                ? 'Enter your FranklinWH app credentials to discover your gateways and batteries.'
                : provider === 'solaredge'
                ? 'Enter your SolarEdge API key to discover your sites. Data is 15‑minute granularity; monitoring only.'
                : 'Enter your Sol-Ark Cloud credentials to automatically discover your plants and equipment.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Inverter provider</Label>
              <Select value={provider} onValueChange={(v: 'solark' | 'eg4' | 'franklinwh' | 'solaredge' | 'solis') => setProvider(v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="solark">Sol-Ark</SelectItem>
                  <SelectItem value="eg4">EG4</SelectItem>
                  <SelectItem value="franklinwh">FranklinWH</SelectItem>
                  <SelectItem value="solaredge">SolarEdge</SelectItem>
                  <SelectItem value="solis">Solis</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {provider === 'solaredge' ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="solaredgeAccount">Account</Label>
                  <Input
                    id="solaredgeAccount"
                    type="text"
                    placeholder="e.g. GreaterTexasSolar, BigTex Solar"
                    value={solaredgeAccount}
                    onChange={(e) => setSolaredgeAccount(e.target.value)}
                    autoComplete="off"
                  />
                  <p className="text-xs text-muted-foreground">
                    A label for this SolarEdge connection so you can tell multiple API keys apart (e.g. one per installer account). Not used for authentication.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="apiKey">API key</Label>
                  <div className="relative">
                    <Input
                      id="apiKey"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Paste your SolarEdge API key"
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      autoComplete="off"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide API key' : 'Show API key'}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Log in at <strong>monitoring.solaredge.com</strong> with your username and password, then go to <strong>Admin → Site Access → API Access</strong> to copy your API key.
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Your API key is encrypted and stored securely. We only use it to fetch monitoring data from SolarEdge.
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="username">
                    {provider === 'eg4' ? 'EG4 Username' : provider === 'franklinwh' ? 'Account' : 'Sol-Ark Username'}
                  </Label>
                  <Input
                    id="username"
                    type={provider === 'franklinwh' ? 'text' : 'email'}
                    placeholder={provider === 'franklinwh' ? 'e.g. your account name' : provider === 'eg4' ? 'EG4 Monitor username' : 'your@email.com'}
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    autoComplete="username"
                  />
                  {provider === 'franklinwh' && (
                    <p className="text-muted-foreground text-sm">Use the exact account you use in the FranklinWH app (case-sensitive).</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">{provider === 'eg4' ? 'EG4 Password' : provider === 'franklinwh' ? 'FranklinWH Password' : 'Sol-Ark Password'}</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Your credentials are encrypted and stored securely. We only use them to fetch data from {provider === 'eg4' ? 'EG4 Monitor' : provider === 'franklinwh' ? 'FranklinWH' : 'Sol-Ark'}.
                  </p>
                </div>
              </>
            )}
            <Button
              onClick={handleDiscover}
              disabled={
                provider === 'solaredge'
                  ? !apiKey.trim() || loading
                  : (!username.trim() || !password.trim() || loading)
              }
              className="w-full"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
              Connect & Discover
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Discovering */}
      {step === 'discover' && loading && (
        <Card className="bg-wt-bg-secondary border-border">
          <CardContent className="py-12 flex flex-col items-center justify-center gap-4">
            <Loader2 className="w-12 h-12 animate-spin text-primary" />
            <div className="text-center">
              <h3 className="text-lg font-semibold">Discovering Your Equipment</h3>
              <p className="text-muted-foreground mt-1">
                {provider === 'eg4' ? 'Connecting to EG4 Monitor and fetching your sites and inverters...'
                  : provider === 'franklinwh' ? 'Connecting to FranklinWH and discovering your gateways...'
                  : provider === 'solaredge' ? 'Connecting to SolarEdge and fetching your sites...'
                  : 'Connecting to Sol-Ark Cloud and fetching your plants and inverters...'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Confirm */}
      {step === 'confirm' && (
        <div className="space-y-6">
          {/* Organization (if new user) */}
          {!organization && (
            <Card className="bg-wt-bg-secondary border-border">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Building className="w-4 h-4 text-primary" />
                  Organization
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="orgName">Organization Name</Label>
                  <Input
                    id="orgName"
                    placeholder="My Home"
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="orgType">Type</Label>
                  <Select value={orgType} onValueChange={(v: 'homeowner' | 'installer' | 'property_manager') => setOrgType(v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="homeowner">Homeowner</SelectItem>
                      <SelectItem value="installer">Solar Installer</SelectItem>
                      <SelectItem value="property_manager">Property Manager</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Discovered Plants (or empty state when 0 sites) */}
          <Card className="bg-wt-bg-secondary border-border flex flex-col min-h-0">
            {plants.length === 0 ? (
              <>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Zap className="w-4 h-4 text-primary" />
                    No sites found
                  </CardTitle>
                  <CardDescription>
                    {error || 'This connection returned no sites to import. You can add another connection or finish onboarding.'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col gap-4">
                  <div className="flex flex-wrap gap-3">
                    <Button
                      variant="outline"
                      onClick={() => { setStep('credentials'); setError(null); setPlants([]); setUsername(''); setPassword(''); setApiKey(''); setSolaredgeAccount(''); }}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Add another connection
                    </Button>
                    <Button
                      onClick={() => setStep(organization ? 'add_another_login' : 'complete')}
                    >
                      <ArrowRight className="w-4 h-4 mr-2" />
                      Finish onboarding
                    </Button>
                  </div>
                </CardContent>
              </>
            ) : (
              <>
            <CardHeader className="pb-3 flex-shrink-0">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Zap className="w-4 h-4 text-primary" />
                  Discovered Plants
                </CardTitle>
                <Badge variant="secondary">
                  {selectedCount} of {plants.length} selected
                </Badge>
              </div>
              <CardDescription>
                We found {plants.length} plant{plants.length !== 1 ? 's' : ''} in your account. Select which ones to import.
              </CardDescription>
              <p className="text-sm font-medium text-foreground mt-2">
                You will only be billed for selected systems.
              </p>
            </CardHeader>
            <CardContent className="min-h-0 flex flex-col p-4 pt-0">
              <div className="min-h-0 h-[60vh] overflow-y-auto pr-2">
                <div className="space-y-3">
                  {plants.map((plant) => {
                    const isExisting = plant.existingInPlatform ?? false;
                    return (
                    <div
                      key={plant.id}
                      role={isExisting ? undefined : 'button'}
                      tabIndex={isExisting ? undefined : 0}
                      onClick={() => !isExisting && handleTogglePlant(plant.id)}
                      onKeyDown={(e) => {
                        if (!isExisting && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault();
                          handleTogglePlant(plant.id);
                        }
                      }}
                      className={`p-4 rounded-lg border transition-colors select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                        isExisting
                          ? 'bg-muted/30 border-border/50 opacity-70 cursor-default'
                          : `cursor-pointer ${plant.selected ? 'bg-primary/5 border-primary/30' : 'bg-secondary/30 border-border/50'}`
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <span onClick={(e) => e.stopPropagation()} className="shrink-0">
                          <Checkbox
                            checked={plant.selected}
                            onCheckedChange={() => !isExisting && handleTogglePlant(plant.id)}
                            disabled={isExisting}
                            className="mt-1"
                          />
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className={`font-semibold ${isExisting ? 'text-muted-foreground' : ''}`}>{plant.name}</h4>
                            <Badge variant="outline" className="text-xs">
                              {plant.inverters.length} inverter{plant.inverters.length !== 1 ? 's' : ''}
                            </Badge>
                            {isExisting && (
                              <Badge variant="secondary" className="text-xs font-normal">
                                Already in platform
                              </Badge>
                            )}
                          </div>
                          
                          {plant.address && (
                            <div className="flex items-center gap-1 mt-1 text-sm text-muted-foreground">
                              <MapPin className="w-3 h-3" />
                              <span className="truncate">{plant.address}</span>
                            </div>
                          )}
                          
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            {plant.batteryCapacityKwh && (
                              <span className="flex items-center gap-1">
                                <Battery className="w-3 h-3" />
                                {plant.batteryCapacityKwh} kWh
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Cpu className="w-3 h-3" />
                              {plant.installedCapacityKw.toFixed(1)} kW
                            </span>
                            {plant.latitude && plant.longitude && (
                              <span className="flex items-center gap-1 text-green-500">
                                <CheckCircle2 className="w-3 h-3" />
                                Location set
                              </span>
                            )}
                          </div>
                          
                          {/* Inverter list */}
                          <div className="mt-3 space-y-1">
                            {plant.inverters.map((inv) => (
                              <div 
                                key={inv.sn}
                                className="flex items-center gap-2 text-xs px-2 py-1 rounded bg-wt-bg-tertiary"
                              >
                                <span className={`w-2 h-2 rounded-full ${inv.isOnline ? 'bg-green-500' : 'bg-muted'}`} />
                                <span className="font-mono">{inv.sn}</span>
                                <Badge variant="secondary" className="text-[10px] px-1">
                                  {inv.role}
                                </Badge>
                                {inv.alias && inv.alias !== inv.sn && (
                                  <span className="text-muted-foreground truncate">{inv.alias}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
              </>
            )}
          </Card>

          {/* EG4: Grid-tied confirmation */}
          {provider === 'eg4' && selectedCount > 0 && (
            <Card className="bg-wt-bg-secondary border-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">System Configuration</CardTitle>
                <CardDescription>
                  Is this system connected to the grid? Grid-primary systems use the grid to back up loads; overnight risk semantics differ.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="eg4GridTied"
                    checked={eg4GridTied}
                    onCheckedChange={(checked) => setEg4GridTied(checked === true)}
                  />
                  <Label htmlFor="eg4GridTied" className="font-normal cursor-pointer">
                    Grid-tied system (grid backs loads)
                  </Label>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Actions (only when there are plants to import) */}
          {plants.length > 0 && (
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={() => {
                setStep('credentials');
                setPlants([]);
              }}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Re-connect
            </Button>
            <Button 
              onClick={handleImport}
              disabled={selectedCount === 0 || (!organization && !orgName.trim()) || loading}
              className="flex-1"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Import {selectedCount} Plant{selectedCount !== 1 ? 's' : ''} ({totalInverters} inverter{totalInverters !== 1 ? 's' : ''})
            </Button>
          </div>
          )}
        </div>
      )}

      {/* Step 4: Importing */}
      {step === 'import' && (
        <Card className="bg-wt-bg-secondary border-border">
          <CardContent className="py-8 flex flex-col items-center justify-center gap-6">
            {importError && (
              <div className="w-full p-4 rounded-md bg-destructive/10 border border-destructive/20 text-destructive flex items-center justify-between gap-2">
                <span>{importError}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setImportError(null);
                    setPollTrigger((n) => n + 1);
                  }}
                >
                  Retry
                </Button>
              </div>
            )}
            <div className="relative">
              <Loader2 className="w-16 h-16 animate-spin text-primary" />
              <span className="absolute inset-0 flex items-center justify-center text-sm font-medium">
                {importProgress}%
              </span>
            </div>
            <div className="text-center space-y-1">
              <h3 className="text-lg font-semibold">Importing systems…</h3>
              <p className="text-muted-foreground">
                {importStatus
                  ? `${importStatus.processed_success} of ${importStatus.total_plants} imported`
                  : 'Starting import…'}
              </p>
              {importStatus && importStatus.failed_count > 0 && (
                <p className="text-sm text-amber-600 dark:text-amber-400">
                  {importStatus.failed_count} failed (you can retry later)
                </p>
              )}
            </div>
            <Progress value={importProgress} className="w-64 h-2" />
          </CardContent>
        </Card>
      )}

      {/* Step 5: Activation (shown once per import batch). Informational only; backfill auto-started. */}
      {step === 'activation' && (
        <Card className="bg-wt-bg-secondary border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-primary" />
              Systems activated
            </CardTitle>
            <CardDescription>
              Your selected systems are now connected. Polling, backfill, and forecasting have started.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 rounded-lg bg-secondary/30 space-y-1 text-sm text-muted-foreground">
              <p>• Backfilling last 30 days</p>
              <p>• Learning in progress</p>
            </div>
            <p className="text-xs text-muted-foreground">No action required. You can continue.</p>
            <Button onClick={() => setStep('add_another_login')} className="w-full">
              Continue
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Step 6: Add Another Login (repeatable). Adding another login never triggers notification setup again. */}
      {step === 'add_another_login' && (
        <Card className="bg-wt-bg-secondary border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building className="w-5 h-5 text-primary" />
              Next step
            </CardTitle>
            <CardDescription>
              Add another provider account to expand your fleet, or continue to set notification defaults.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <Button variant="outline" onClick={() => { setStep('credentials'); setPlants([]); setUsername(''); setPassword(''); setApiKey(''); setSolaredgeAccount(''); }}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Add another login
            </Button>
            <Button onClick={async () => {
              const { data: prefs } = await supabase.from('notification_preferences').select('id').eq('user_id', user?.id ?? '').is('property_id', null).limit(1);
              if (prefs?.length) setStep('complete');
              else setStep('notification_preferences');
            }}>
              Continue
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Step 7: Notification Defaults (once). Fleet-level; one of Enable or Skip must be chosen. */}
      {step === 'notification_preferences' && (
        <div className="space-y-6">
          <Card className="bg-wt-bg-secondary border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                Notification defaults
              </CardTitle>
              <CardDescription>
                These are default alerts for all systems. You can fine-tune per site later.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Default alerts: low battery forecast, system offline, high curtailment. Channels: push, email.
              </p>
              <div className="flex flex-col gap-3">
                <Button
                  onClick={async () => {
                    setLoading(true);
                    setError(null);
                    try {
                      const email = (notificationEmail?.trim() || user?.email) ?? '';
                      await supabase.from('notification_preferences').upsert(
                        [
                          { user_id: user!.id, property_id: null, channel: 'email', severity_min: 'critical', enabled: true, email_override: email || null, updated_at: new Date().toISOString() },
                          ...(notificationPhone.trim() ? [{ user_id: user!.id, property_id: null, channel: 'sms', severity_min: 'critical', enabled: true, phone_for_sms: notificationPhone.trim(), sms_consent_at: new Date().toISOString(), updated_at: new Date().toISOString() }] : []),
                        ],
                        { onConflict: 'user_id,property_id,channel' }
                      );
                      const propId = Array.isArray(importedProperties) && importedProperties.length > 0 ? importedProperties[0].id : null;
                      if (propId) await supabase.functions.invoke('send-channel-verification', { body: { propertyId: propId } });
                      setStep('complete');
                    } catch (err) {
                      setError(err instanceof Error ? err.message : 'Failed to save preferences');
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading || !user?.id}
                >
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Enable notifications
                </Button>
                <Button
                  variant="outline"
                  onClick={async () => {
                    setLoading(true);
                    setError(null);
                    try {
                      await supabase.from('notification_preferences').upsert(
                        [{ user_id: user!.id, property_id: null, channel: 'email', severity_min: 'critical', enabled: false, updated_at: new Date().toISOString() }],
                        { onConflict: 'user_id,property_id,channel' }
                      );
                      setStep('complete');
                    } catch (err) {
                      setError(err instanceof Error ? err.message : 'Failed to save preferences');
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading || !user?.id}
                >
                  Skip for now
                </Button>
              </div>
              <div className="space-y-2 pt-2">
                <Label htmlFor="notification-email">Email (optional, for Enable)</Label>
                <Input id="notification-email" type="email" placeholder="you@example.com" value={notificationEmail} onChange={(e) => setNotificationEmail(e.target.value)} autoComplete="email" />
                <Label htmlFor="notification-phone">Phone for SMS (optional)</Label>
                <Input id="notification-phone" type="tel" placeholder="+1 555 000 0000" value={notificationPhone} onChange={(e) => setNotificationPhone(e.target.value)} autoComplete="tel" />
              </div>
            </CardContent>
          </Card>
          <Button variant="ghost" onClick={() => setStep('add_another_login')}>
            Back
          </Button>
        </div>
      )}

      {/* Step 8: Complete (Go Live) */}
      {step === 'complete' && (
        <Card className="bg-wt-bg-secondary border-border">
          <CardContent className="py-12 flex flex-col items-center justify-center gap-6">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
              <CheckCircle2 className="w-10 h-10 text-green-500" />
            </div>
            <div className="text-center">
              <h3 className="text-xl font-semibold">Setup complete</h3>
              <p className="text-muted-foreground mt-1">
                Successfully imported {Array.isArray(importedProperties) ? importedProperties.length : 0} propert{Array.isArray(importedProperties) && importedProperties.length !== 1 ? 'ies' : 'y'}.
              </p>
              {activationBackfillStarted && (
                <p className="text-xs text-muted-foreground mt-2">
                  Historical data is importing in the background. You can track progress in Settings.
                </p>
              )}
            </div>
            
            <div className="w-full max-w-sm space-y-2">
              {Array.isArray(importedProperties)
                ? importedProperties.map((prop) => (
                    <div
                      key={prop.id}
                      className="flex items-center gap-3 p-3 rounded-lg bg-secondary/30"
                    >
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                      <div className="flex-1">
                        <p className="font-medium">{prop.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {Array.isArray(prop.systemIds) ? prop.systemIds.length : 0} inverter{Array.isArray(prop.systemIds) && prop.systemIds.length !== 1 ? 's' : ''} connected
                        </p>
                      </div>
                    </div>
                  ))
                : null}
            </div>
            
            {error && (
              <div className="flex items-center gap-2 text-sm text-destructive mt-2">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <Button onClick={handleComplete} size="lg" className="mt-4" disabled={loading}>
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ArrowRight className="w-4 h-4 mr-2" />
              )}
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
